import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { boxAt, boxCell, boxLever, boxSet, boxTrain, cellCenter, craneWeight, gargOn, type BattleState, type GearboxRules } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** The crane: an A-frame, its arm out, the hook. */
const CRANE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 14L5 3l2 11M5 3h9M13 3v4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M11.5 8.5h3v2.5h-3z" fill="currentColor"/></svg>';
/** A gargoyle sitting (wings folded). */
const GARG = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3l2 2-1 1 4 1-2 3 1 4H4l1-4-2-3 4-1-1-1z" fill="currentColor"/></svg>';
/** The car on its cable. */
const CAR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1 3.5l14-1" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M8 3v3" stroke="currentColor" stroke-width="1.3"/><rect x="3" y="6" width="10" height="6" rx="1.2" fill="currentColor"/><rect x="9" y="7.3" width="2.6" height="2" fill="#0c0e12"/></svg>';
/** The deck tilting. */
const TILT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 11l12-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path d="M10 11.5l3-0.8-1-3" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/><circle cx="5" cy="7.5" r="1.4" fill="currentColor"/></svg>';
/** A gear. */
const GEAR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.6l1.2 1.8 2.1-.6.4 2.2 2.1.8-.9 2 .9 2-2.1.8-.4 2.2-2.1-.6L8 14.4l-1.2-1.8-2.1.6-.4-2.2-2.1-.8.9-2-.9-2 2.1-.8.4-2.2 2.1.6z" fill="currentColor"/><circle cx="8" cy="8" r="2.1" fill="#0c0e12"/></svg>';
/** The windmill. */
const MILL = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 8L3 3M8 8l5-5M8 8l-5 5M8 8l5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle cx="8" cy="8" r="1.5" fill="currentColor"/></svg>';
/** The drum and its cable. */
const DRUM = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2.5" y="5" width="7" height="7" rx="1.4" fill="currentColor"/><path d="M9.5 6.5L15 4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** La Picotera's beak. */
const BEAK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 7c2-3 6-4 9-2l4 4-5-1c-2 2-5 2-8 0z" fill="currentColor"/><circle cx="6" cy="6.6" r="0.9" fill="#0c0e12"/></svg>';

/** The way a turning slot goes, by the sim's sign (+1 the motor's). */
const arrow = (dir: number): string => (dir > 0 ? '↻' : '↺');
/** A speed in tenths of a turn a minute, as it reads (one decimal only when it has one). */
const speed = (tenths: number): string => (tenths % 10 === 0 ? String(tenths / 10) : (tenths / 10).toLocaleString(document.documentElement.lang || undefined, { maximumFractionDigits: 1 }));
/** Seconds to a tick, at least 1. */
const secs = (s: BattleState, at: number): number => Math.max(1, Math.ceil((at - s.tick) / TICK_HZ));

type Mark = { el: HTMLButtonElement; teeth: HTMLElement; state: HTMLElement; slot: number; point: THREE.Vector3 };

/**
 * The cable on screen (docs/sendas/artifice.md, chapter 2), inside a path's own plate (game/senda-hud.ts) and over the
 * board; a phone's plate kept under about a quarter of the screen:
 *
 * - the crane (`crane`): the piece on the hook (of three) and its way down, or why Dunna stopped the winch; the weight on
 *   the arm against what tips it (red pips; one at the far end weighs two), and, swaying, how long till it lets go.
 * - the car (`gondola`): the way across, its pylons marked; who sits on the cable against what stops the car; a tilt
 *   warned in red with its side and its count («¡Agarrate de un poste!»), and stopped, the next flock.
 * - the gearbox (`gearbox`, the test): the motor's 60 and its way; what the drum asks (red, with the cable's way) and
 *   what it does now (red while wrong, white when right); what is in the hands; the wrong pulls; La Picotera's next
 *   peck and wing-beat. Over the board, a mark on each slot (A, B, B, C, R: its gear's teeth, its way and speed), on
 *   each gear on the shelf (its teeth), and on the clutch's lever. A tap on a mark sends the hero: what is in its hands
 *   and what is there change places, or it pulls.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class CableHud {
  readonly panel: HTMLElement;
  private kind: 'crane' | 'gondola' | 'gearbox';
  private v = new THREE.Vector3();
  private key = '';
  private rowA = h('div.bh-row');
  private textA = h('span.al-wave');
  private rowB = h('div.bh-row.al-waverow');
  private textB = h('span.al-wave');
  private pipsB: HTMLElement[] = [];
  private rowC = h('div.bh-row.al-waverow');
  private textC = h('span.al-wave');
  private bar = h('i.cb-bar');
  private wantEl = h('b.tl-want');
  private nowEl = h('span.tl-now');
  private marks: Mark[] = [];

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const sr = s.rules?.senda;
    this.kind = sr?.gearbox ? 'gearbox' : sr?.gondola ? 'gondola' : 'crane';
    const rows: HTMLElement[] = [];
    if (this.kind === 'crane') {
      this.rowA.append(h('span.bh-ico', { html: CRANE }), this.textA, h('span.cb-track', {}, this.bar));
      this.pipsB = Array.from({ length: sr?.crane?.tilt ?? 3 }, () => h('i'));
      this.rowB.append(h('span.bh-ico', { html: GARG }), h('span.bh-label', {}, t('senda.cable.arm')), h('span.al-pips.cb-pips', { title: t('senda.cable.armHint') }, ...this.pipsB), this.textB);
      this.rowC.append(h('span.bh-ico', { html: GARG }), this.textC);
      rows.push(this.rowA, this.rowB, this.rowC);
    } else if (this.kind === 'gondola') {
      const pylons = (sr?.gondola?.pylons ?? []).map((p) => h('b.cb-pylon', { style: `left:${Math.round((p * 100) / (sr?.gondola?.len ?? 1))}%` }));
      this.rowA.append(h('span.bh-ico', { html: CAR }), this.textA, h('span.cb-track', {}, this.bar, ...pylons));
      this.pipsB = Array.from({ length: sr?.gondola?.stop ?? 5 }, () => h('i'));
      this.rowB.append(h('span.bh-ico', { html: GARG }), h('span.bh-label', {}, t('senda.cable.onCable')), h('span.al-pips.cb-pips', { title: t('senda.cable.onCableHint') }, ...this.pipsB), this.textB);
      this.rowC.append(h('span.bh-ico', { html: TILT }), this.textC);
      rows.push(this.rowA, this.rowB, this.rowC);
    } else if (sr?.gearbox) {
      const r = sr.gearbox;
      const mill = h('div.bh-row.tl-wheel', {}, h('span.bh-ico', { html: MILL }), h('span.bh-label', {}, t('senda.cable.motor')), h('b.tl-speed', {}, `${r.input} ${arrow(1)}`));
      this.wantEl.textContent = t('senda.cable.asks', { n: r.want, way: arrow(r.way) });
      this.rowA.classList.add('tl-drum');
      this.rowA.append(h('span.bh-ico', { html: DRUM }), h('span.bh-label', {}, t('senda.cable.drum')), this.wantEl, this.nowEl);
      this.rowB.append(h('span.bh-ico', { html: GEAR }), this.textB);
      this.rowC.append(h('span.bh-ico', { html: BEAK }), h('span.bh-label', {}, t('senda.cable.boss')), this.textC);
      rows.push(mill, this.rowA, this.rowB, this.rowC);
      this.buildMarks(s, r);
    }
    this.panel = h(`div.bh.tl-panel.cb-panel.cb-${this.kind}`, {}, ...rows);
  }

  /** A mark on each slot, each gear's lying spot and the lever. */
  private buildMarks(s: BattleState, r: GearboxRules): void {
    const R = s.map.radius;
    const names = ['A', 'B', 'B', 'C', 'R'];
    const n = boxLever(r) + 1;
    for (let slot = 0; slot < n; slot++) {
      const c = cellCenter(R, boxCell(r, slot));
      const kind = slot < 5 ? 'axle' : slot === boxLever(r) ? 'lever' : 'lying';
      const teeth = h('b.tl-teeth');
      const state = h('small.al-state.tl-state');
      // (The slots' letters are painted on the box's bed: only the lever's name over the board.)
      const name = kind === 'lever' ? t('senda.cable.lever') : '';
      void names;
      const hint = kind === 'axle' ? 'senda.cable.slotHint' : kind === 'lever' ? 'senda.cable.leverHint' : 'senda.cable.gearHint';
      const el = h(
        `button.al-stone.tl-slot.cb-slot.${kind}${slot === 3 ? '.drum' : ''}${slot === 4 ? '.idler' : ''}`,
        { type: 'button', 'data-test': kind === 'lever' ? 'box-lever' : `box-slot-${slot}`, 'aria-label': t(hint), title: t(hint), onpointerup: (e: Event) => (e.stopPropagation(), this.mark(slot)) },
        h('span.al-ring'),
        teeth,
        name ? h('b.mn-name.tl-name', {}, name) : '',
        state,
      ) as HTMLButtonElement;
      this.board.append(el);
      this.marks.push({ el, teeth, state, slot, point: simToWorld(c.x, c.y, new THREE.Vector3(), 0.3) });
    }
  }

  private updateCrane(s: BattleState): void {
    const r = s.rules?.senda?.crane;
    const w = s.senda?.crane;
    if (!r || !w) return;
    const d = s.senda?.npcs.find((n) => n.id === 'dunna');
    const piece = Math.min(r.pieces, w.done + 1);
    const a =
      w.end >= 0
        ? t('senda.cable.allDown')
        : w.sway >= 0
          ? t('senda.cable.stopped', { n: piece, of: r.pieces })
          : d && d.flinch > 0
            ? t('senda.cable.flinch', { n: piece, of: r.pieces })
            : t('senda.cable.lowering', { n: piece, of: r.pieces });
    if (this.textA.textContent !== a) this.textA.textContent = a;
    setStyle(this.bar, 'width', `${Math.round(w.end >= 0 ? 100 : (w.prog * 100) / r.lower)}%`);
    this.rowA.classList.toggle('warn', w.sway >= 0);
    const weight = craneWeight(s);
    this.pipsB.forEach((p, i) => p.classList.toggle('on', i < weight));
    const sway = w.sway >= 0 ? Math.max(1, Math.ceil((r.fall - w.sway) / TICK_HZ)) : -1;
    const b = sway > 0 ? t('senda.cable.sway', { s: sway }) : '';
    if (this.textB.textContent !== b) this.textB.textContent = b;
    this.rowB.classList.toggle('warn', w.sway >= 0);
    const next = Math.max(0, Math.ceil((w.flock - s.waveT) / TICK_HZ));
    const c = w.end >= 0 ? '' : t('senda.cable.flockIn', { s: next });
    if (this.textC.textContent !== c) this.textC.textContent = c;
    this.rowC.hidden = !c;
    this.rowC.classList.toggle('warn', next <= 3);
  }

  private updateGondola(s: BattleState): void {
    const r = s.rules?.senda?.gondola;
    const w = s.senda?.gondola;
    if (!r || !w) return;
    const k = Math.floor((w.prog * 100) / r.len);
    const a = w.end >= 0 ? t('senda.cable.arrived') : w.stopped ? t('senda.cable.carStopped', { n: k }) : t('senda.cable.way', { n: k });
    if (this.textA.textContent !== a) this.textA.textContent = a;
    setStyle(this.bar, 'width', `${Math.min(100, k)}%`);
    this.rowA.classList.toggle('warn', w.stopped);
    const on = gargOn(s, 2);
    this.pipsB.forEach((p, i) => p.classList.toggle('on', i < on));
    const b = w.stopped ? t('senda.cable.flockIn', { s: Math.max(1, Math.ceil((r.stopFlock - w.still) / TICK_HZ)) }) : '';
    if (this.textB.textContent !== b) this.textB.textContent = b;
    this.rowB.classList.toggle('warn', w.stopped || on >= r.stop - 1);
    const side = t(w.side > 0 ? 'senda.cable.south' : 'senda.cable.north');
    const c = w.warn >= 0 ? (s.tick < w.tip ? t('senda.cable.tiltIn', { side, s: secs(s, w.tip) }) : t('senda.cable.tilting', { side })) : t('senda.cable.calm');
    if (this.textC.textContent !== c) this.textC.textContent = c;
    this.rowC.classList.toggle('warn', w.warn >= 0);
  }

  private updateGearbox(s: BattleState): void {
    const r = s.rules?.senda?.gearbox;
    const w = s.senda?.gearbox;
    if (!r || !w) return;
    const set = boxSet(r, w);
    const train = boxTrain(set);
    const right = w.right >= 0;
    const drumDir = train.dir[3] ?? 0;
    const drumSp = train.speed[3] ?? 0;
    const now = right ? t('senda.cable.sings') : train.full ? t('senda.cable.now', { n: speed(drumSp), way: arrow(drumDir) }) : t('senda.cable.loose');
    if (this.nowEl.textContent !== now) this.nowEl.textContent = now;
    const ok = !right && train.full && drumSp === r.want * 10 && drumDir === r.way;
    this.rowA.classList.toggle('right', right || ok);
    this.rowA.classList.toggle('wrong', !right && train.full && !ok);
    const errs = w.errors ? ` · ${t(w.errors === 1 ? 'senda.cable.errOne' : 'senda.cable.errs', { n: w.errors })}` : '';
    const hand = right ? t('senda.cable.hauled') : w.hand >= 0 ? t('senda.cable.inHand', { n: r.pieces[w.hand] ?? 0 }) : ok ? t('senda.cable.pull') : t('senda.cable.handFree');
    const b = hand + errs;
    if (this.textB.textContent !== b) this.textB.textContent = b;
    this.rowB.classList.toggle('good', ok);
    const c = w.down >= 0 ? t('senda.cable.bossDown') : w.beatAt >= 0 ? t('senda.cable.beat', { s: secs(s, w.beatAt) }) : w.peckSlot >= -1 ? t(w.peckSlot < 0 ? 'senda.cable.peckYou' : 'senda.cable.peckGear', { s: secs(s, w.peck) }) : t('senda.cable.watching');
    if (this.textC.textContent !== c) this.textC.textContent = c;
    this.rowC.classList.toggle('warn', w.down < 0 && (w.beatAt >= 0 || w.peckSlot >= -1));
    // The marks: their teeth, their way and speed.
    const key = `${w.on.join(',')}|${w.hand}|${w.right}|${w.job}`;
    if (key !== this.key) {
      this.key = key;
      for (const m of this.marks) {
        const lever = m.slot === boxLever(r);
        const k = lever ? -1 : boxAt(w, m.slot);
        const teeth = k >= 0 ? String(r.pieces[k]) : '';
        if (m.teeth.textContent !== teeth) m.teeth.textContent = teeth;
        const axle = m.slot < 5;
        const dir = axle ? (train.dir[m.slot] ?? 0) : 0;
        const st = axle && k >= 0 ? (dir ? `${arrow(dir)} ${speed(train.speed[m.slot] ?? 0)}` : t('senda.cable.stillMark')) : m.slot === 3 && k < 0 ? t('senda.cable.asks', { n: r.want, way: arrow(r.way) }) : lever ? (ok ? t('senda.cable.pullMark') : '') : '';
        if (m.state.textContent !== st) {
          m.state.textContent = st;
          remeasureMark(m.el);
        }
        m.el.classList.toggle('full', k >= 0);
        m.el.classList.toggle('turning', dir !== 0);
        m.el.classList.toggle('ready', lever && ok);
        for (const n of [12, 18, 24, 36]) m.el.classList.toggle(`t${n}`, k >= 0 && r.pieces[k] === n);
        // A lying spot with nothing on it shows only with a gear in the hands (somewhere to put it down).
        m.el.classList.toggle('gone', right || (!axle && !lever && k < 0 && w.hand < 0));
      }
    }
    for (const m of this.marks) {
      const sent = w.job === m.slot;
      m.el.classList.toggle('sent', sent);
      const need = m.slot === boxLever(r) ? r.pull : r.stand;
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, need)).toFixed(2) : '0');
      m.el.disabled = s.result !== 'running';
    }
  }

  update(s: BattleState): void {
    if (this.kind === 'crane') this.updateCrane(s);
    else if (this.kind === 'gondola') this.updateGondola(s);
    else this.updateGearbox(s);
  }

  /** Every frame: the marks over their places. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
